import { Controller } from '@hotwired/stimulus';
import consumer from "../channels/consumer";

// Subscribes to an ActionCable channel and refreshes the page (or a specific
// Turbo Frame) when the server broadcasts a { message: "finished" } payload.
//
// Usage — full-page refresh (integrations, websites, etc.):
//
//   <div data-controller="realtime-refresh"
//        data-realtime-refresh-channel-value="MyChannel"
//        data-realtime-refresh-subscription-key-value="record_id"
//        data-realtime-refresh-subscription-id-value="<%= @record.id %>">
//   </div>
//
// Usage — targeted Turbo Frame refresh:
//
//   <div data-controller="realtime-refresh"
//        data-realtime-refresh-channel-value="MyChannel"
//        data-realtime-refresh-subscription-key-value="record_id"
//        data-realtime-refresh-subscription-id-value="<%= @record.id %>"
//        data-realtime-refresh-frame-value="my_frame">
//
//     <turbo-frame id="my_frame">
//       <!-- refreshable content -->
//     </turbo-frame>
//   </div>
//
// Pausing frame refreshes with data-realtime-refresh-paused:
//
//   When a Turbo Frame swaps between different views (i.e. a list and a form),
//   an automatic refresh can yank the user out of a form they're filling in.
//   Add `data-realtime-refresh-paused` to any content that should NOT be
//   auto-refreshed. The controller skips the refresh when this marker is
//   present inside the frame.
//
//   <!-- list partial (refreshes normally, no attribute needed) -->
//   <div class="my-list">
//     ...
//   </div>
//
//   <!-- form partial (refresh is paused while this is in the frame) -->
//   <div class="my-form" data-realtime-refresh-paused>
//     ...
//   </div>
//
export default class extends Controller {
  static values = {
    channel: String,
    subscriptionKey: String,
    subscriptionId: String,
    frame: String,
  }

  connect() {
    if (!this.hasChannelValue || !this.hasSubscriptionKeyValue || !this.hasSubscriptionIdValue) return;

    const subscription = {
      channel: this.channelValue,
      [this.subscriptionKeyValue]: this.subscriptionIdValue
    };

    this.channel = consumer.subscriptions.create(subscription, {
      received: this.handleReceived.bind(this),
      connected: () => {},
      disconnected: () => {}
    });
  }

  handleReceived(data) {
    if (data.message !== 'finished') return;

    if (this.hasFrameValue) {
      const frame = document.getElementById(this.frameValue);
      if (!frame) return;
      if (frame.querySelector('[data-realtime-refresh-paused]')) return;

      frame.src = window.location.pathname;
      return;
    }

    if (window.Turbo && typeof window.Turbo.visit === 'function') {
      window.Turbo.visit(window.location.href, { action: 'replace' });
    } else {
      window.location.reload();
    }
  }

  disconnect() {
    if (this.channel) this.channel.unsubscribe();
  }
}
